import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CHROMATIC_PRESET_ORDER,
  CHROMATIC_PRESETS,
  parseFlexibleColorToRgba,
  resolveChromaticColors,
  toOpaqueHexForColorInput,
} from '../../../tools/chromaticPresets';
import type { ChromaticFixedPresetId, ChromaticPresetId } from '../../../tools/chromaticPresets';
import { isChromaticContrastLowVsExamBackground } from '../../../tools/chromaticContrast';
import type { dyopsType } from '../../../types/dyops';
import strings from '../../../localization';
import Radio from '../../atoms/radio';
import './index.scss';

const CHROMATIC_PRESET_RADIO_NAME = 'chromatic-preset';

interface ChromaticPresetPanelProps {
  dyops: dyopsType;
  disabled: boolean;
  onCommit: (next: dyopsType) => void;
  /** Compact copy and no title row (e.g. inside modal). */
  compact?: boolean;
  /** Strip outer chrome when nested in modal. */
  variant?: 'default' | 'embedded';
  /** When set with embedded+compact, warning flags sync here so a fixed footer can render them. */
  onChromaticWarningsChange?: (state: { contrastLow: boolean; customInvalid: boolean }) => void;
  /** Live preview in modal without persisting doctor preference on every keystroke. */
  onPreviewDyopsChange?: (next: dyopsType) => void;
}

// Renders examiner chromatic presets, custom hex, and native color pickers for custom mode.
const ChromaticPresetPanel: FC<ChromaticPresetPanelProps> = ({
  dyops,
  disabled,
  onCommit,
  compact = false,
  variant = 'default',
  onChromaticWarningsChange,
  onPreviewDyopsChange,
}) => {
  const [customPrimary, setCustomPrimary] = useState(dyops.chromaticPrimary);
  const [customSecondary, setCustomSecondary] = useState(dyops.chromaticSecondary);

  useEffect(() => {
    if (dyops.chromaticPresetId === 'custom') {
      setCustomPrimary(dyops.chromaticPrimary);
      setCustomSecondary(dyops.chromaticSecondary);
    }
  }, [dyops.chromaticPresetId, dyops.chromaticPrimary, dyops.chromaticSecondary]);

  const isCustom = dyops.chromaticPresetId === 'custom';

  const primaryForPicker = useMemo(() => {
    const src = isCustom ? customPrimary : dyops.chromaticPrimary;
    return toOpaqueHexForColorInput(src) ?? '#000000';
  }, [isCustom, customPrimary, dyops.chromaticPrimary]);

  const secondaryForPicker = useMemo(() => {
    const src = isCustom ? customSecondary : dyops.chromaticSecondary;
    return toOpaqueHexForColorInput(src) ?? '#000000';
  }, [isCustom, customSecondary, dyops.chromaticSecondary]);

  const resolved = useMemo(
    () =>
      resolveChromaticColors(
        dyops.chromaticPresetId as ChromaticPresetId,
        dyops.chromaticPrimary,
        dyops.chromaticSecondary,
      ),
    [dyops.chromaticPresetId, dyops.chromaticPrimary, dyops.chromaticSecondary],
  );

  const contrastLow = useMemo(() => {
    if (!resolved) return false;
    return isChromaticContrastLowVsExamBackground(resolved.primary, resolved.secondary, 'admin');
  }, [resolved]);

  const emitPreview = useCallback(
    (presetId: ChromaticPresetId, primary: string, secondary: string) => {
      if (!onPreviewDyopsChange) {
        return;
      }
      onPreviewDyopsChange({
        ...dyops,
        chromaticPresetId: presetId,
        chromaticPrimary: primary,
        chromaticSecondary: secondary,
      });
    },
    [dyops, onPreviewDyopsChange],
  );

  const commitPair = useCallback(
    (presetId: ChromaticPresetId, primary: string, secondary: string) => {
      const next = {
        ...dyops,
        chromaticPresetId: presetId,
        chromaticPrimary: primary,
        chromaticSecondary: secondary,
      };
      emitPreview(presetId, primary, secondary);
      onCommit(next);
    },
    [dyops, onCommit, emitPreview],
  );

  const handlePresetClick = (id: ChromaticFixedPresetId) => {
    if (disabled) return;
    const pair = CHROMATIC_PRESETS[id];
    commitPair(id, pair.primary, pair.secondary);
  };

  const handleModeCustom = () => {
    if (disabled) return;
    const p = (isCustom ? customPrimary : dyops.chromaticPrimary).trim();
    const s = (isCustom ? customSecondary : dyops.chromaticSecondary).trim();
    setCustomPrimary(p);
    setCustomSecondary(s);
    commitPair('custom', p, s);
  };

  // Handles one shared radio group: fixed preset ids or custom mode.
  const handlePresetRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const v = e.target.value;
    if (v === 'custom') {
      handleModeCustom();
    } else {
      handlePresetClick(v as ChromaticFixedPresetId);
    }
  };

  const handleApplyCustom = () => {
    if (disabled) return;
    const p = customPrimary.trim();
    const s = customSecondary.trim();
    if (!parseFlexibleColorToRgba(p) || !parseFlexibleColorToRgba(s)) return;
    commitPair('custom', p, s);
  };

  const handlePrimaryPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !isCustom) return;
    const p = e.target.value.toUpperCase();
    setCustomPrimary(p);
    emitPreview('custom', p, customSecondary);
  };

  const handleSecondaryPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !isCustom) return;
    const s = e.target.value.toUpperCase();
    setCustomSecondary(s);
    emitPreview('custom', customPrimary, s);
  };

  const customInvalid =
    isCustom && (!parseFlexibleColorToRgba(customPrimary.trim()) || !parseFlexibleColorToRgba(customSecondary.trim()));

  useEffect(() => {
    if (!(variant === 'embedded' && compact) || !onChromaticWarningsChange) return;
    onChromaticWarningsChange({
      contrastLow: !!(contrastLow && resolved),
      customInvalid,
    });
  }, [variant, compact, onChromaticWarningsChange, contrastLow, resolved, customInvalid]);

  /** Builds radio label with a dual-color swatch and accessible text. */
  const renderPresetLabel = (text: string, primary: string, secondary: string) => (
    <>
      <span className="chromatic-preset__swatch" aria-hidden="true">
        <span className="chromatic-preset__swatch-half" style={{ backgroundColor: primary }} />
        <span className="chromatic-preset__swatch-half" style={{ backgroundColor: secondary }} />
      </span>
      <span className="chromatic-preset__preset-label">{text}</span>
    </>
  );

  /** Custom option: palette badge; idle rainbow when not selected so it does not look like a duplicate preset. */
  const renderCustomLabel = () => (
    <>
      <span className="chromatic-preset__swatch-wrap" aria-hidden="true">
        {isCustom ? (
          <span className="chromatic-preset__swatch">
            <span className="chromatic-preset__swatch-half" style={{ backgroundColor: primaryForPicker }} />
            <span className="chromatic-preset__swatch-half" style={{ backgroundColor: secondaryForPicker }} />
          </span>
        ) : (
          <span className="chromatic-preset__swatch chromatic-preset__swatch--custom-idle" />
        )}
        <span className="chromatic-preset__custom-badge">
          <svg viewBox="0 0 16 16" width="12" height="12" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <rect x="1" y="1" width="6" height="6" rx="1.2" fill="#ef5350" />
            <rect x="9" y="1" width="6" height="6" rx="1.2" fill="#42a5f5" />
            <rect x="1" y="9" width="6" height="6" rx="1.2" fill="#66bb6a" />
            <rect x="9" y="9" width="6" height="6" rx="1.2" fill="#ffca28" />
          </svg>
        </span>
      </span>
      <span className="chromatic-preset__preset-label">{strings.chromatic.modeCustom}</span>
    </>
  );

  return (
    <div
      className={`chromatic-preset${disabled ? ' chromatic-preset--disabled' : ''}${
        variant === 'embedded' ? ' chromatic-preset--embedded' : ''
      }`}
      aria-label={strings.chromatic.heading}
    >
      {!compact ? <div className="chromatic-preset__heading">{strings.chromatic.heading}</div> : null}
      {!compact ? <p className="chromatic-preset__hint">{strings.chromatic.beforeStartHint}</p> : null}
      <div
        className={`chromatic-preset__presets chromatic-preset__presets--radio-row${
          variant === 'embedded' ? ' chromatic-preset__presets--radio-row--modal' : ''
        }`}
      >
        {CHROMATIC_PRESET_ORDER.map((id) => {
          const pair = CHROMATIC_PRESETS[id];
          return (
            <Radio
              key={id}
              label={renderPresetLabel(strings.chromatic.presets[id], pair.primary, pair.secondary)}
              name={CHROMATIC_PRESET_RADIO_NAME}
              id={`chromatic-preset-${id}`}
              value={id}
              checked={!isCustom && dyops.chromaticPresetId === id}
              onChange={handlePresetRadioChange}
              disabled={disabled}
              shell
            />
          );
        })}
        <div className="chromatic-preset__option--custom">
          <Radio
            label={renderCustomLabel()}
            name={CHROMATIC_PRESET_RADIO_NAME}
            id="chromatic-preset-custom"
            value="custom"
            checked={isCustom}
            onChange={handlePresetRadioChange}
            disabled={disabled}
            shell
          />
        </div>
      </div>
      <div className="chromatic-preset__custom">
        <div className="chromatic-preset__custom-fields">
          <div className="chromatic-preset__label">
            <span>{strings.chromatic.primaryLabel}</span>
            <div className="chromatic-preset__custom-row">
              <input
                type="color"
                className="chromatic-preset__color-picker"
                value={primaryForPicker}
                onChange={handlePrimaryPickerChange}
                onBlur={handleApplyCustom}
                disabled={disabled || !isCustom}
                aria-label={strings.chromatic.pickRing}
                title={strings.chromatic.pickRing}
              />
              <input
                className="chromatic-preset__input chromatic-preset__input--grow"
                value={isCustom ? customPrimary : dyops.chromaticPrimary}
                disabled={disabled || !isCustom}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomPrimary(v);
                  emitPreview('custom', v, customSecondary);
                }}
                onBlur={handleApplyCustom}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
              />
            </div>
          </div>
          <div className="chromatic-preset__label">
            <span>{strings.chromatic.secondaryLabel}</span>
            <div className="chromatic-preset__custom-row">
              <input
                type="color"
                className="chromatic-preset__color-picker"
                value={secondaryForPicker}
                onChange={handleSecondaryPickerChange}
                onBlur={handleApplyCustom}
                disabled={disabled || !isCustom}
                aria-label={strings.chromatic.pickGap}
                title={strings.chromatic.pickGap}
              />
              <input
                className="chromatic-preset__input chromatic-preset__input--grow"
                value={isCustom ? customSecondary : dyops.chromaticSecondary}
                disabled={disabled || !isCustom}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomSecondary(v);
                  emitPreview('custom', customPrimary, v);
                }}
                onBlur={handleApplyCustom}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
              />
            </div>
          </div>
        </div>
      </div>
      {!(variant === 'embedded' && compact) && customInvalid && isCustom ? (
        <div className="chromatic-preset__warn">{strings.chromatic.invalidHex}</div>
      ) : null}
      {!(variant === 'embedded' && compact) && contrastLow && resolved ? (
        <div className="chromatic-preset__warn chromatic-preset__warn--contrast">
          <span className="doctor-test__chromatic-modal-foot-warning">
            <span className="doctor-test__chromatic-modal-foot-warning-icon" aria-hidden="true" />
            <span className="doctor-test__chromatic-modal-foot-warning-text">
              <span className="doctor-test__chromatic-modal-foot-warning-line">
                {strings.chromatic.contrastWarningLine1}
              </span>
              <span className="doctor-test__chromatic-modal-foot-warning-continued">
                {strings.chromatic.contrastWarningLine2}
              </span>
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default ChromaticPresetPanel;
